import React, { useState, useEffect, useContext } from 'react';
import { createVehicle } from '../../../../services/api';
import { AuthContext } from '../../../../context/AuthContext';

const VehiclesTable = () => {
  // Get vehicles data and functions from context
  const { 
    getAllVehicles, 
    allVehicles, 
    allVehiclesLoading, 
    allVehiclesError 
  } = useContext(AuthContext);

  // Local state for UI and form management
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [pagination, setPagination] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    vehicleName: "",
    vehicleNumber: "",
    typeOfVehical: "",
    ManagerName: "",
    address: "",
    mobileNumber: "",
    notes: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // Vehicle types for filter dropdown and form
  const vehicleTypes = ['Residential', 'Commercial', 'Other'];

  // Fetch vehicles on component mount
  useEffect(() => {
    if ((!allVehicles || !Array.isArray(allVehicles) || allVehicles.length === 0) && !allVehiclesLoading) {
      getAllVehicles();
    }
  }, [getAllVehicles, allVehicles, allVehiclesLoading]);

  // Filter and paginate vehicles when data or filters change
  useEffect(() => {
    if (!allVehicles || !Array.isArray(allVehicles)) {
      setFilteredVehicles([]);
      setPagination(null);
      return;
    }

    let filtered = [...allVehicles];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(vehicle => 
        vehicle.vehicleName?.toLowerCase().includes(searchLower) ||
        vehicle.vehicleNumber?.toLowerCase().includes(searchLower) ||
        vehicle.ManagerName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filterType) {
      filtered = filtered.filter(vehicle => vehicle.typeOfVehical === filterType);
    }

    // Implement pagination
    const itemsPerPage = 10;
    const totalEntries = filtered.length;
    const totalPages = Math.ceil(totalEntries / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedVehicles = filtered.slice(startIndex, endIndex);

    setFilteredVehicles(paginatedVehicles);
    setPagination({
      totalEntries,
      totalPages,
      currentPage,
      hasPrevPage: currentPage > 1,
      hasNextPage: currentPage < totalPages
    });
  }, [allVehicles, searchTerm, filterType, currentPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setFilterType('');
    setCurrentPage(1);
    getAllVehicles();
  };

  const handleAddVehicle = () => {
    setShowAddModal(true);
    // Reset form when opening modal
    setFormData({
      vehicleName: "",
      vehicleNumber: "",
      typeOfVehical: "",
      ManagerName: "",
      address: "",
      mobileNumber: "",
      notes: "",
    });
    setFormErrors({});
    setSuccessMessage("");
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setFormData({
      vehicleName: "",
      vehicleNumber: "",
      typeOfVehical: "",
      ManagerName: "",
      address: "",
      mobileNumber: "",
      notes: "",
    });
    setFormErrors({});
    setSuccessMessage("");
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
    
    // Clear success message when form is modified
    if (successMessage) {
      setSuccessMessage("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.vehicleName.trim()) {
      newErrors.vehicleName = "Vehicle name is required";
    }

    if (!formData.vehicleNumber.trim()) {
      newErrors.vehicleNumber = "Vehicle number is required";
    } else if (formData.vehicleNumber.length < 4) {
      newErrors.vehicleNumber = "Vehicle number must be at least 4 characters";
    }

    // Mobile number validation (if provided)
    if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Mobile number must be exactly 10 digits";
    }

    // Vehicle number format validation (basic)
    if (formData.vehicleNumber && !/^[A-Za-z0-9]+$/.test(formData.vehicleNumber)) {
      newErrors.vehicleNumber = "Vehicle number should only contain letters and numbers";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setFormLoading(true);
    setSuccessMessage("");
    
    try {
      // Prepare data for submission
      const submitData = {
        vehicleName: formData.vehicleName.trim(),
        vehicleNumber: formData.vehicleNumber.trim().toUpperCase(),
        typeOfVehical: formData.typeOfVehical.trim(),
        ManagerName: formData.ManagerName.trim(),
        address: formData.address.trim(),
        mobileNumber: formData.mobileNumber.trim(),
        notes: formData.notes.trim(),
      };

      const response = await createVehicle(submitData);
      
      if (response.success) {
        setSuccessMessage("Vehicle created successfully!");
        
        // Reset form
        setFormData({
          vehicleName: "",
          vehicleNumber: "",
          typeOfVehical: "",
          ManagerName: "",
          address: "",
          mobileNumber: "",
          notes: "",
        });
        setFormErrors({});
        
        // Refresh vehicle list and close modal after a short delay
        setTimeout(() => {
          getAllVehicles(); // Use context function to refresh
          setShowAddModal(false);
          setSuccessMessage("");
        }, 2000);
      }
    } catch (error) {
      console.error("Error creating vehicle:", error);
      
      // Handle specific error cases
      if (error.message && error.message.includes("already exists")) {
        setFormErrors({ vehicleNumber: error.message });
      } else {
        setFormErrors({ general: error.message || "Failed to create vehicle. Please try again." });
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormReset = () => {
    setFormData({
      vehicleName: "",
      vehicleNumber: "",
      typeOfVehical: "",
      ManagerName: "",
      address: "",
      mobileNumber: "",
      notes: "",
    });
    setFormErrors({});
    setSuccessMessage("");
  };

  // Handle loading state from context
  if (allVehiclesLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading vehicles...</span>
      </div>
    );
  }

  // Handle error state from context
  if (allVehiclesError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <div className="flex">
          <div className="text-red-600">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-2">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{allVehiclesError}</p>
            <button 
              onClick={getAllVehicles}
              className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 bg-white">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-6 w-6 text-gray-700"
              >
                <path d="M14 16V8a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2Z"/>
                <path d="M15 2c-1.35 0-2.5.5-2.5 1.5S13.65 5 15 5s2.5-.5 2.5-1.5S16.35 2 15 2Z"/>
                <path d="M17 6.5c-1 0-1.5.5-1.5 1.5S16 9.5 17 9.5s1.5-.5 1.5-1.5S18 6.5 17 6.5Z"/>
              </svg>
              <h2 className="text-2xl font-bold text-gray-900">Vehicle Management</h2>
              {allVehiclesLoading && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              )}
            </div>
            
            {/* Add Vehicle Button */}
            <button 
              onClick={handleAddVehicle}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Add Vehicle
            </button>
          </div>
          
          {pagination && (
            <p className="text-sm text-gray-600">
              Total vehicles: {pagination.totalEntries} | Page {pagination.currentPage} of {pagination.totalPages}
            </p>
          )}
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search vehicles by name, number, or manager..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          
          <div className="sm:w-48">
            <select
              value={filterType}
              onChange={handleFilterChange}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">All Vehicle Types</option>
              {vehicleTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          
          <button 
            onClick={handleRefresh}
            disabled={allVehiclesLoading}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {allVehiclesLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Loading...
              </div>
            ) : (
              'Refresh'
            )}
          </button>
        </div>

        {/* Table */}
        {!allVehicles || !Array.isArray(allVehicles) || allVehicles.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No vehicles found matching your criteria.</p>
            {allVehicles && Array.isArray(allVehicles) && allVehicles.length === 0 && (
              <button 
                onClick={handleAddVehicle}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Add First Vehicle
              </button>
            )}
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p>No vehicles found matching your search criteria.</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manager
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVehicles.map((vehicle) => (
                  <tr key={vehicle._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{vehicle.vehicleName}</div>
                        <div className="text-sm text-blue-600 font-mono">{vehicle.vehicleNumber}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        vehicle.typeOfVehical === 'Car' ? 'bg-green-100 text-green-800' :
                        vehicle.typeOfVehical === 'Truck' ? 'bg-blue-100 text-blue-800' :
                        vehicle.typeOfVehical === 'Motorcycle' ? 'bg-yellow-100 text-yellow-800' :
                        vehicle.typeOfVehical === 'Bus' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {vehicle.typeOfVehical || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{vehicle.ManagerName || 'N/A'}</div>
                      {vehicle.address && (
                        <div className="text-sm text-gray-500 truncate max-w-xs" title={vehicle.address}>
                          {vehicle.address}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vehicle.mobileNumber ? (
                        <div className="text-sm text-gray-900 font-mono">{vehicle.mobileNumber}</div>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      {vehicle.notes ? (
                        <div className="text-sm text-gray-500 truncate" title={vehicle.notes}>
                          {vehicle.notes}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No notes</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(vehicle.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing page {pagination.currentPage} of {pagination.totalPages}
            </div>
            
            <div className="flex space-x-2">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className={`px-3 py-1 text-sm rounded ${
                  pagination.hasPrevPage 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const pageNum = Math.max(1, currentPage - 2) + i;
                if (pageNum <= pagination.totalPages) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 text-sm rounded ${
                        pageNum === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className={`px-3 py-1 text-sm rounded ${
                  pagination.hasNextPage 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal for Add Vehicle Form */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseModal}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              {/* Modal header */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Add New Vehicle
                  </h3>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Form content */}
                <div className="bg-white p-6 border border-gray-200 shadow-lg rounded-xl max-w-4xl mx-auto">
                  <div className="mb-6">
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-6 w-6 text-gray-700"
                      >
                        <path d="M14 16V8a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2Z"/>
                        <path d="M15 2c-1.35 0-2.5.5-2.5 1.5S13.65 5 15 5s2.5-.5 2.5-1.5S16.35 2 15 2Z"/>
                        <path d="M17 6.5c-1 0-1.5.5-1.5 1.5S16 9.5 17 9.5s1.5-.5 1.5-1.5S18 6.5 17 6.5Z"/>
                      </svg>
                      <h2 className="text-2xl font-semibold text-gray-800">Add New Vehicle</h2>
                    </div>
                    <p className="text-gray-600 mt-1">Register a new vehicle in the system</p>
                  </div>

                  {/* Success Message */}
                  {successMessage && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex">
                        <div className="text-green-600">
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-green-800">{successMessage}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* General Error Message */}
                  {formErrors.general && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex">
                        <div className="text-red-600">
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-red-800">{formErrors.general}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Vehicle Name */}
                      <div className="space-y-2">
                        <label htmlFor="vehicleName" className="text-sm font-medium text-gray-700">
                          Vehicle Name *
                        </label>
                        <input
                          id="vehicleName"
                          name="vehicleName"
                          type="text"
                          placeholder="Enter vehicle name (e.g., Honda Civic)"
                          value={formData.vehicleName}
                          onChange={handleFormChange}
                          required
                          className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                            formErrors.vehicleName ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {formErrors.vehicleName && <p className="text-red-500 text-xs">{formErrors.vehicleName}</p>}
                      </div>

                      {/* Vehicle Number */}
                      <div className="space-y-2">
                        <label htmlFor="vehicleNumber" className="text-sm font-medium text-gray-700">
                          Vehicle Number *
                        </label>
                        <input
                          id="vehicleNumber"
                          name="vehicleNumber"
                          type="text"
                          placeholder="Enter vehicle number (e.g., MH12AB1234)"
                          value={formData.vehicleNumber}
                          onChange={handleFormChange}
                          required
                          className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 uppercase ${
                            formErrors.vehicleNumber ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {formErrors.vehicleNumber && <p className="text-red-500 text-xs">{formErrors.vehicleNumber}</p>}
                      </div>

                      {/* Vehicle Type */}
                      <div className="space-y-2">
                        <label htmlFor="typeOfVehical" className="text-sm font-medium text-gray-700">
                          Type of Vehicle
                        </label>
                        <select
                          id="typeOfVehical"
                          name="typeOfVehical"
                          value={formData.typeOfVehical}
                          onChange={handleFormChange}
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          <option value="">Select Vehicle Type</option>
                          {vehicleTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Manager Name */}
                      <div className="space-y-2">
                        <label htmlFor="ManagerName" className="text-sm font-medium text-gray-700">
                          Manager Name
                        </label>
                        <input
                          id="ManagerName"
                          name="ManagerName"
                          type="text"
                          placeholder="Enter manager name"
                          value={formData.ManagerName}
                          onChange={handleFormChange}
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      </div>

                      {/* Mobile Number */}
                      <div className="space-y-2">
                        <label htmlFor="mobileNumber" className="text-sm font-medium text-gray-700">
                          Mobile Number
                        </label>
                        <input
                          id="mobileNumber"
                          name="mobileNumber"
                          type="tel"
                          placeholder="Enter 10-digit mobile number"
                          value={formData.mobileNumber}
                          onChange={handleFormChange}
                          maxLength="10"
                          className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                            formErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {formErrors.mobileNumber && <p className="text-red-500 text-xs">{formErrors.mobileNumber}</p>}
                      </div>

                      {/* Address */}
                      <div className="space-y-2">
                        <label htmlFor="address" className="text-sm font-medium text-gray-700">
                          Address
                        </label>
                        <input
                          id="address"
                          name="address"
                          type="text"
                          placeholder="Enter address"
                          value={formData.address}
                          onChange={handleFormChange}
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                      <label htmlFor="notes" className="text-sm font-medium text-gray-700">
                        Notes
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        placeholder="Enter any additional notes about the vehicle"
                        value={formData.notes}
                        onChange={handleFormChange}
                        rows={4}
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <button
                        type="button"
                        onClick={handleFormSubmit}
                        disabled={formLoading}
                        className={`flex-1 py-3 rounded-md transition-colors duration-200 font-medium ${
                          formLoading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        } text-white`}
                      >
                        {formLoading ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Creating Vehicle...
                          </div>
                        ) : (
                          'Create Vehicle'
                        )}
                      </button>
                      
                      <button
                        type="button"
                        onClick={handleFormReset}
                        disabled={formLoading}
                        className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reset Form
                      </button>
                    </div>

                    {/* Form Info */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex">
                        <div className="text-blue-600">
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-2">
                          <p className="text-sm text-blue-800">
                            <span className="font-medium">Note:</span> Fields marked with * are required. Vehicle number will be automatically converted to uppercase and must be unique.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VehiclesTable;